import { CalendarDays, History, Lock, Maximize2, Pencil, Trash2, User, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { fmtDate, fmtDateTime } from '../../../shared/format'
import { useContacts } from '@/features/contacts/api'
import { useAddNote } from '../../api'
import type { Meeting } from '../../types'
import { toastError, useCrm } from '../../ui'
import { ChannelLabel, ResultBadge } from '../CrmBits'
import { MeetingExpenses } from '../expenses/MeetingExpenses'
import { MeetingFiles } from './MeetingFiles'
import { MeetingMinutesDoc } from './MeetingMinutesDoc'
import { useRecordMeetingView } from '../../meetingFiles'
import { MeetingVisibilityBar } from './MeetingVisibility'
import { MeetingMinutes } from './MeetingMinutes'
import { MeetingRecord } from './MeetingRecord'

type Props = { meeting: Meeting; canWrite: boolean; onClose: () => void; onEdit: () => void; onDelete: () => void; onNew: (plan: boolean) => void }

export function MeetingDetail({ meeting: m, canWrite: canWriteProp, onClose, onEdit, onDelete, onNew }: Props) {
  const { t } = useTranslation()
  // İçeriği gizlenen toplantıda (kısıtlı/gizli, katılımcı ya da yetkili değil) düzenleme yok.
  const canWrite = canWriteProp && !m.restricted
  useRecordMeetingView(m.id, !m.restricted)
  const { staffName, staffTitle } = useCrm()
  const contacts = useContacts(m.customerId)
  const attendees = (contacts.data ?? []).filter((c) => m.contactIds.includes(c.id))
  const extraStaff = m.staffIds.filter((id) => id !== m.staffId)
  const addNote = useAddNote()
  const [note, setNote] = useState('')

  const saveNote = () => {
    if (note.trim().length < 2) return
    addNote.mutate({ meetingId: m.id, body: note }, { onSuccess: () => setNote(''), onError: toastError })
  }

  return (
    <aside className="flex flex-col rounded-xl border bg-card shadow-card" aria-label={t('crm.meeting.detail')}>
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <h2 className="flex-1 font-semibold">{t('crm.meeting.detail')}</h2>
        {canWrite ? (
          <>
            <Button size="icon" variant="ghost" className="size-8" onClick={onEdit} aria-label={t('common.edit')}>
              <Pencil />
            </Button>
            <Button size="icon" variant="ghost" className="size-8 hover:text-destructive" onClick={onDelete} aria-label={t('common.delete')}>
              <Trash2 />
            </Button>
          </>
        ) : null}
        <Button size="icon" variant="ghost" className="size-8" onClick={onClose} aria-label={t('common.close')}>
          <X />
        </Button>
      </header>
      <div className="space-y-5 p-4">
        <MeetingVisibilityBar meeting={m} />
        <p className="flex items-center gap-2 text-sm font-medium">
          <CalendarDays className="size-4 text-primary" aria-hidden />
          {fmtDate(m.date)} · {m.start}
          {m.end ? ` – ${m.end}` : ''}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <Avatar name={staffName(m.staffId)} className="size-9" />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{staffName(m.staffId)}</span>
              <span className="block truncate text-xs text-muted-foreground">{staffTitle(m.staffId)}</span>
            </span>
          </span>
          <ChannelLabel channel={m.channel} />
        </div>
        {extraStaff.length || attendees.length ? (
          <div className="space-y-1 text-sm">
            {extraStaff.length ? (
              <p>
                <span className="font-medium">{t('crm.meeting.otherStaff')}:</span> <span className="text-muted-foreground">{extraStaff.map(staffName).join(', ')}</span>
              </p>
            ) : null}
            {attendees.length ? (
              <p>
                <span className="font-medium">{t('crm.meeting.customerAttendees')}:</span> <span className="text-muted-foreground">{attendees.map((c) => c.full_name).join(', ')}</span>
              </p>
            ) : null}
          </div>
        ) : null}
        {m.restricted ? (
          <p className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
            <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
            {t(m.visibility === 'private' ? 'crm.meeting.privateLocked' : 'crm.meeting.restrictedLocked')}
          </p>
        ) : (
          <>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{t('crm.fields.result')}</span>
          <ResultBadge result={m.result} />
        </div>
        <div>
          <h3 className="text-sm font-semibold">{t('crm.fields.summary')}</h3>
          <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{m.summary}</p>
        </div>
        {m.customerResponse ? (
          <div>
            <h3 className="text-sm font-semibold">{t('crm.meeting.customerResponse')}</h3>
            <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{m.customerResponse}</p>
          </div>
        ) : null}
        {m.decisions ? (
          <div>
            <h3 className="text-sm font-semibold">{t('crm.meeting.decisions')}</h3>
            <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{m.decisions}</p>
          </div>
        ) : null}
        {m.result !== 'not_reached' ? (
          <Button variant="outline" className="w-full" asChild>
            <Link to={`/customers/${m.customerId}/live/${m.id}`}>
              <Maximize2 />
              {t('crm.live.open')}
            </Link>
          </Button>
        ) : null}
        <MeetingMinutes meeting={m} canWrite={canWrite} />
        <MeetingRecord meeting={m} canWrite={canWrite} />
        <MeetingFiles meeting={m} canEdit={canWrite} attendees={(contacts.data ?? []).map((c) => ({ id: c.id, name: c.full_name }))} />
        <MeetingMinutesDoc meeting={m} contacts={(contacts.data ?? []).map((c) => ({ id: c.id, name: c.full_name, email: c.email ?? '' }))} />
        {m.nextAction ? (
          <div className="rounded-lg bg-muted/40 p-3">
            <h3 className="text-sm font-semibold">{t('crm.fields.nextAction')}</h3>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1 font-medium">
                <CalendarDays className="size-4" aria-hidden />
                {fmtDate(m.nextAction.date)}
              </span>
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <User className="size-4" aria-hidden />
                {staffName(m.nextAction.staffId)}
              </span>
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{m.nextAction.text}</p>
          </div>
        ) : null}

        <MeetingExpenses meeting={m} />

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{t('crm.meeting.notes')}</h3>
          {m.notes.map((n) => (
            <div key={n.id} className="flex gap-2">
              <Avatar name={n.author} className="size-8" />
              <div className="min-w-0">
                <p className="text-sm">
                  <span className="font-medium">{n.author}</span> <span className="text-xs text-muted-foreground">{fmtDateTime(n.at)}</span>
                </p>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{n.body}</p>
              </div>
            </div>
          ))}
          {canWrite ? (
            <div className="flex gap-2">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder={t('crm.meeting.notePlaceholder')} aria-label={t('crm.meeting.notePlaceholder')} />
              <Button size="sm" variant="outline" className="self-end" onClick={saveNote} disabled={note.trim().length < 2 || addNote.isPending}>
                {t('crm.meeting.addNote')}
              </Button>
            </div>
          ) : null}
        </section>

        {m.history.length ? (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">{t('crm.meeting.history')}</h3>
            <ol className="space-y-1.5">
              {m.history.map((h) => (
                <li key={h.id} className="flex items-start gap-2.5 rounded-lg bg-muted/40 px-3 py-2 text-xs">
                  <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-card text-muted-foreground shadow-xs">
                    <History className="size-3.5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium">{t(`crm.meetingHistory.${h.text}`, { defaultValue: h.text })}</p>
                    <p className="text-muted-foreground">
                      <span className="tabular-nums">{fmtDateTime(h.at)}</span> · {h.by}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ) : null}
          </>
        )}
      </div>
      {canWrite ? (
        <div className="mt-auto grid grid-cols-2 gap-2 border-t p-4">
          <Button onClick={() => onNew(false)}>{t('crm.meeting.new')}</Button>
          <Button variant="outline" onClick={() => onNew(true)}>
            <CalendarDays />
            {t('crm.meeting.plan')}
          </Button>
        </div>
      ) : null}
    </aside>
  )
}
